import type { ReceivedReviewRole } from "@/lib/reviews/schema";

/**
 * Canonical seller-then-buyer rating pick — the ONE place this fallback lives.
 *
 * The stored aggregates (`profile_review_stats.as_seller_*` / `as_buyer_*`)
 * are computed exclusively in SQL (migrations 20260525130000 /
 * 20260526120000 — published reviews only, `round(avg, 2)`); this module does
 * no averaging, it only decides WHICH bucket a profile's headline rating
 * comes from: seller reviews when any exist, otherwise buyer reviews,
 * otherwise none. Previously this decision was duplicated in
 * `hooks/use-party-review-stats.ts` and the mobile sibling hook (role-pick
 * drift risk). Pure + client-safe: no server imports, usable from hooks,
 * routes and `lib/profile-stats.ts` alike.
 */
export type PartyRatingSource = {
  as_seller_avg?: number | null;
  as_seller_count?: number | null;
  as_buyer_avg?: number | null;
  as_buyer_count?: number | null;
};

export type PartyRating = {
  /** Stored 2-decimal average, unrounded (display layers `toFixed(1)` it). */
  rating: number | null;
  reviewCount: number | null;
  role: ReceivedReviewRole | null;
};

export function pickPartyRating(
  stats: PartyRatingSource | null | undefined,
): PartyRating {
  const sellerCount = stats?.as_seller_count ?? 0;
  const buyerCount = stats?.as_buyer_count ?? 0;

  if (sellerCount > 0) {
    const avg = stats?.as_seller_avg;
    return {
      rating: typeof avg === "number" && Number.isFinite(avg) ? avg : null,
      reviewCount: sellerCount,
      role: "seller",
    };
  }

  if (buyerCount > 0) {
    const avg = stats?.as_buyer_avg;
    return {
      rating: typeof avg === "number" && Number.isFinite(avg) ? avg : null,
      reviewCount: buyerCount,
      role: "buyer",
    };
  }

  return { rating: null, reviewCount: null, role: null };
}

/**
 * One-decimal rounding for wire payloads (cross-platform contract: `rating`
 * ships as a 1-decimal number). Matches what every display layer renders via
 * `rating.toFixed(1)` — the stored 2-decimal value never reaches user rows.
 */
export function roundRatingOneDecimal(rating: number | null): number | null {
  return rating === null ? null : Math.round(rating * 10) / 10;
}
